import type { LanguageCode } from '../types/types';

export interface LevelTitle {
  level: number;
  name: string;
  icon: string;
  description: string;
  min: number;
}

const THRESHOLDS = [0, 250, 750, 1500, 2500, 4000, 6000, 8500, 11500, 15500, 20500, 28000];

const raw: Record<LanguageCode, Array<{ name: string; icon: string; description: string }>> = {
  wf: [
    { name: 'Taalibé',      icon: '🧒', description: "L'élève qui commence son voyage dans les langues" },
    { name: 'Jeli',         icon: '🥁', description: "Le griot, gardien vivant de la parole et de la mémoire" },
    { name: 'Karamoko',     icon: '📜', description: "L'érudit, maître des mots et du savoir" },
    { name: 'Donso',        icon: '🏹', description: "Le chasseur, explorateur des sens et des cultures" },
    { name: 'Simbo',        icon: '🦁', description: "Le maître chasseur, porteur d'une sagesse forgée par l'expérience" },
    { name: 'Koro',         icon: '🧓', description: "L'aîné respecté, source de sagesse pour la communauté" },
    { name: 'Tierno',       icon: '📖', description: "L'homme de savoir qui ouvre les portes de la compréhension" },
    { name: 'Almami',       icon: '☪️',  description: "Le guide de la communauté, gardien des traditions" },
    { name: 'Fama',         icon: '⚖️',  description: "Le chef, souverain de son domaine de connaissance" },
    { name: 'Naaba',        icon: '🏰', description: "Le roi, dont la sagesse s'étend à l'horizon" },
    { name: 'Mansa',        icon: '👑', description: "L'empereur, maître absolu des langues et cultures africaines" },
    { name: 'Buur bu Réew', icon: '⭐', description: "Le roi du pays — titre suprême des souverains wolof" },
  ],
  ba: [
    { name: 'Dɔgɔkɔrɔ',    icon: '🧒', description: "Le petit qui commence, curieux du monde bambara" },
    { name: 'Karɛbolo',     icon: '📚', description: "L'élève assidu, avide de mots et de sens" },
    { name: 'Barakɛla',     icon: '⚒️',  description: "Le travailleur, qui forge son savoir avec persévérance" },
    { name: 'Donso',        icon: '🏹', description: "Le chasseur, explorateur des savanes et des savoirs" },
    { name: 'Donsotigui',   icon: '🦁', description: "Le maître chasseur, guide de ceux qui cherchent" },
    { name: 'Koro',         icon: '🧓', description: "L'aîné, dont la parole porte le poids de l'expérience" },
    { name: 'Farima',       icon: '🛡️',  description: "L'homme de valeur, respecté par sa communauté" },
    { name: 'Jeli',         icon: '🥁', description: "Le griot, mémoire vivante du peuple mandé" },
    { name: 'Dugukolotigi', icon: '🏡', description: "Le chef de village, gardien de l'harmonie collective" },
    { name: 'Fama',         icon: '⚔️',  description: "Le roi, dont l'autorité rayonne sur tout son peuple" },
    { name: 'Mansa',        icon: '👑', description: "L'empereur, héritier de la gloire de Mansa Moussa" },
    { name: 'Mansa Mawdo',  icon: '⭐', description: "Le grand empereur — titre suprême des souverains mandé" },
  ],
  la: [
    { name: 'Mwana',         icon: '🧒', description: "L'enfant qui ouvre les yeux sur la langue lingala" },
    { name: 'Molakisi',      icon: '📚', description: "Celui qui apprend, attentif aux rythmes du Congo" },
    { name: 'Moteki',        icon: '💡', description: "L'éveillé, dont l'esprit s'ouvre aux savoirs" },
    { name: 'Nganga',        icon: '🌿', description: "Le sage guérisseur, porteur de connaissance" },
    { name: 'Nkumu',         icon: '🛡️',  description: "Le notable, reconnu pour sa maîtrise des mots" },
    { name: 'Mobali Makasi', icon: '💪', description: "L'homme fort, pilier de sa communauté" },
    { name: 'Mokonzi',       icon: '🔑', description: "Le chef, dont la voix guide et rassemble" },
    { name: 'Mokonzi Mkubwa',icon: '🏛️', description: "Le grand chef, respecté au-delà de son village" },
    { name: 'Ntotila',       icon: '🏰', description: "Le roi, souverain de son domaine de connaissance" },
    { name: 'Mwene',         icon: '🦅', description: "Le grand souverain, dont la sagesse dépasse les frontières" },
    { name: 'Bokonzi',       icon: '👑', description: "Le suprême, maître absolu de la langue et de la culture" },
    { name: 'Ntinu',         icon: '⭐', description: "Le roi suprême — titre des grands souverains du royaume Kongo" },
  ],
  ff: [
    { name: 'Cukalel',         icon: '🧒', description: "L'enfant peul, qui découvre les premiers mots" },
    { name: 'Garowal',         icon: '🐄', description: "Le jeune berger, gardien du troupeau" },
    { name: 'Jom Ladde',       icon: '🌾', description: "Le maître des pâturages, patient et attentif" },
    { name: 'Pulaar',          icon: '💬', description: "Celui qui maîtrise la parole peule" },
    { name: 'Gando',           icon: '📖', description: "L'homme de savoir, instruit dans les traditions peules" },
    { name: 'Ardo',            icon: '🏕️', description: "Le chef de clan, guide des familles nomades" },
    { name: 'Jaoro',           icon: '⚖️',  description: "Le notable, dont la justice est reconnue par tous" },
    { name: 'Lamido',          icon: '🏛️', description: "Le gouverneur, autorité respectée de son territoire" },
    { name: 'Almaami',         icon: '☪️',  description: "Le guide spirituel, lumière de la communauté" },
    { name: 'Mawdo',           icon: '🦅', description: "Le grand, dont la stature domine les hommes et les savoirs" },
    { name: 'Elimaan',         icon: '👑', description: "L'éclairé, maître absolu de la langue et de la foi" },
    { name: 'Laamiiɗo Mawɗo', icon: '⭐', description: "Le grand souverain — titre suprême des rois peuls" },
  ],
  co: [
    { name: 'Sheri',        icon: '🧒', description: "L'enfant, qui fait ses premiers pas dans la langue copte" },
    { name: 'Mathetes',     icon: '📚', description: "Le disciple, avide d'apprendre les mots ancestraux" },
    { name: 'Anagnostes',   icon: '📜', description: "Le lecteur, qui déchiffre les textes sacrés" },
    { name: 'Psalt',        icon: '🎵', description: "Le chantre, dont la voix porte les prières coptes" },
    { name: 'Diakon',       icon: '✝️',  description: "Le diacre, serviteur engagé de la langue et de la foi" },
    { name: 'Ieros',        icon: '🕯️', description: "Le consacré, gardien des rites et des traditions" },
    { name: 'Papas',        icon: '🙏', description: "Le père spirituel, guide de sa communauté" },
    { name: 'Ieromonakhos', icon: '⛪', description: "Le moine, retiré dans la contemplation et le savoir" },
    { name: 'Episkopos',    icon: '💎', description: "L'évêque, dont l'autorité rayonne sur son diocèse" },
    { name: 'Hegumenos',    icon: '🏛️', description: "L'abbé, maître spirituel d'une communauté entière" },
    { name: 'Patriarkhos',  icon: '👑', description: "Le patriarche, gardien suprême de la langue copte" },
    { name: 'Baba',         icon: '⭐', description: "Le pape — titre suprême du patriarche de l'Église copte d'Alexandrie" },
  ],
  sw: [
    { name: 'Mtoto',        icon: '🧒', description: "L'enfant, qui découvre les premiers mots swahili" },
    { name: 'Mwanafunzi',   icon: '📚', description: "L'étudiant, curieux et appliqué" },
    { name: 'Msomi',        icon: '📜', description: "L'érudit, qui maîtrise les subtilités de la langue" },
    { name: 'Mwalimu',      icon: '🎓', description: "L'enseignant, qui transmet le savoir aux autres" },
    { name: 'Shujaa',       icon: '⚔️',  description: "Le héros courageux, défenseur de sa culture" },
    { name: 'Mzee',         icon: '🧓', description: "L'aîné sage, dont l'expérience guide la communauté" },
    { name: 'Bwana Mkubwa', icon: '🗝️', description: "Le grand maître, respecté sur toute la côte swahili" },
    { name: 'Jumbe',        icon: '🏡', description: "Le chef local, garant de l'ordre et des traditions" },
    { name: 'Sultan',       icon: '🌙', description: "Le sultan, souverain de son domaine de connaissance" },
    { name: 'Mfalme',       icon: '🏰', description: "Le roi, dont la sagesse s'étend à l'horizon" },
    { name: 'Sultani Mkuu', icon: '👑', description: "Le grand sultan, maître absolu de la langue swahili" },
    { name: 'Mwinyi Mkuu',  icon: '⭐', description: "Le grand seigneur — titre suprême des souverains de la côte swahili" },
  ],
  ka: [
    { name: 'Sedjemy',    icon: '🧒', description: "Celui qui écoute, l'élève qui fait ses premiers pas dans les hiéroglyphes" },
    { name: 'Sesh Shed',  icon: '📚', description: "L'apprenti-scribe, qui copie ses premiers signes" },
    { name: 'Sesh',       icon: '📜', description: "Le scribe, maître de l'écriture sacrée" },
    { name: 'Sesh-Medjat',icon: '🖋️',  description: "Le scribe des archives, gardien des textes" },
    { name: 'Wab',        icon: '🕯️', description: "Le prêtre-pur, admis dans l'enceinte du temple" },
    { name: 'Hem-netjer', icon: '⚱️',  description: "Le serviteur du dieu, prêtre du culte quotidien" },
    { name: 'Sem',        icon: '🔱', description: "Le grand-prêtre, officiant des rites funéraires" },
    { name: 'Imy-r Per',  icon: '🏛️', description: "L'intendant, gestionnaire d'un grand domaine" },
    { name: 'Hery-tep',   icon: '🛡️',  description: "Le chef, autorité reconnue de sa province" },
    { name: 'Tjaty',      icon: '⚖️',  description: "Le vizir, bras droit du roi" },
    { name: 'Nesu',       icon: '👑', description: "Le roi, souverain des Deux Terres" },
    { name: 'Nesu-Bity',  icon: '⭐', description: "Roi de Haute et Basse-Égypte — titre suprême du pharaon" },
  ],
  yo: [
    { name: 'Ọmọdé',      icon: '🧒', description: "L'enfant, qui découvre les premiers mots yoruba" },
    { name: 'Akẹ́kọ̀ọ́',    icon: '📚', description: "L'élève, curieux et appliqué" },
    { name: 'Onílù',      icon: '🥁', description: "Le tambourinaire, qui fait parler le tambour dùndún" },
    { name: 'Ọ̀mọ̀wé',     icon: '📜', description: "L'érudit, qui maîtrise les tons et les subtilités de la langue" },
    { name: 'Olùkọ́',      icon: '🎓', description: "L'enseignant, qui transmet le savoir aux autres" },
    { name: 'Akíkanjú',   icon: '⚔️',  description: "Le brave, défenseur de sa culture" },
    { name: 'Àgbà',       icon: '🧓', description: "L'aîné sage, dont la parole guide la communauté" },
    { name: 'Babaláwo',   icon: '🔮', description: "Le gardien du savoir d'Ifá, maître des proverbes" },
    { name: 'Ìjòyè',      icon: '🏅', description: "Le chef titré, honoré par la communauté" },
    { name: 'Baálẹ̀',      icon: '🏡', description: "Le chef de la ville, garant de l'ordre et des traditions" },
    { name: 'Ọba',        icon: '👑', description: "Le roi, dont la sagesse s'étend sur tout le royaume" },
    { name: 'Aláàfin',    icon: '⭐', description: "Le souverain d'Ọ̀yọ́ — titre suprême des rois yoruba" },
  ],
};

export function getLevelTitles(languageCode: LanguageCode): LevelTitle[] {
  const titles = raw[languageCode] ?? raw.wf;
  return titles.map((t, i) => ({
    level: i + 1,
    name: t.name,
    icon: t.icon,
    description: t.description,
    min: THRESHOLDS[i],
  }));
}

const PRESTIGE_STEP = 10000;

export function getLevel(xp: number, languageCode: LanguageCode) {
  const levels = getLevelTitles(languageCode);
  let current = levels[0];
  for (const lvl of levels) {
    if (xp >= lvl.min) current = lvl;
  }
  const idx = levels.indexOf(current);
  const namedNext = levels[idx + 1] ?? null;

  // Progression infinie au-delà du niveau 12 (prestige)
  if (!namedNext) {
    const overflow = xp - current.min;
    const prestigeSteps = Math.floor(overflow / PRESTIGE_STEP);
    const currentMin = current.min + prestigeSteps * PRESTIGE_STEP;
    const nextMin = currentMin + PRESTIGE_STEP;
    const prestigeCurrent: LevelTitle = {
      level: current.level + prestigeSteps,
      name: current.name,
      icon: prestigeSteps === 0 ? current.icon : '✦',
      description: current.description,
      min: currentMin,
    };
    const prestigeNext: LevelTitle = {
      level: prestigeCurrent.level + 1,
      name: current.name,
      icon: '✦',
      description: current.description,
      min: nextMin,
    };
    const progress = Math.round(((xp - currentMin) / PRESTIGE_STEP) * 100);
    return { current: prestigeCurrent, next: prestigeNext, progress };
  }

  const progress = Math.min(100, Math.round(((xp - current.min) / (namedNext.min - current.min)) * 100));
  return { current, next: namedNext, progress };
}
